// Tatmeen/SSCCAggregation/aggregationThunk.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { postData } from "../../../apiservice/apis";
import { getInvoiceBySeachApi } from "../../../apiservice/setting";

/**
 * Fetch purchase vouchers with SSCCs using existing invoice search API
 */
export const fetchPurchaseVouchers = createAsyncThunk(
  "ssccAggregation/fetchPurchaseVouchers",
  async ({ searchTerm = "" }, { rejectWithValue }) => {
    try {
      const res = await getInvoiceBySeachApi({
        search: searchTerm
      });

      return res.data || [];
    } catch (err) {
      return rejectWithValue(
        err?.message || "Failed to fetch purchase vouchers"
      );
    }
  }
);

export const fetchAvailableSSCCCodes = createAsyncThunk(
  "ssccAggregation/fetchAvailableSSCCCodes",
  async (purchaseId, { rejectWithValue }) => {
    try {
      const res = await postData(
        "/purchase/available-sscc-codes",
        {
          purchase_id: purchaseId
        }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(
        err.message || "Failed to fetch SSCC codes"
      );
    }
  }
);

export const createCSSSCC = createAsyncThunk(
  "ssccAggregation/createCSSSCC",
  async (
    {
      purchaseId,
      ssccCode
    },
    { rejectWithValue }
  ) => {
    try {

      const res = await postData(
        "/purchase/create-cs-sscc",
        {
          purchase_id: purchaseId,
          sscc_code: ssccCode
        }
      );
      if (res.data.error) {
        throw new Error(res.data.message);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.message ||
        "Failed to create CS SSCC"
      );

    }
  }
);

/**
 * Fetch purchase SSCCs and hierarchy
 */
export const fetchPurchaseSSCCs = createAsyncThunk(
  "ssccAggregation/fetchPurchaseSSCCs",
  async (purchaseId, { rejectWithValue }) => {
    try {

      const res = await postData(
        "/purchase/available-sscc-codes",
        {
          purchase_id: purchaseId
        }
      );

      return {
        masterSSCC: res.data.masterSSCC || [],
        beSSCCs: res.data.beSSCCs || [],
        csSSCCs: res.data.csSSCCs || [],
        aggregations: res.data.aggregations || []
      };

    } catch (err) {

      return rejectWithValue(
        err.message || "Failed to fetch SSCC data"
      );

    }
  }
);

export const fetchUnpackStatus =
  createAsyncThunk(
    "ssccAggregation/fetchUnpackStatus",

    async (
      purchaseId,
      { rejectWithValue }
    ) => {
      try {

        const res =
          await postData(
            "/purchase/unpack-status",
            {
              purchase_id:
                purchaseId
            }
          );

        if (
          res.data.error
        ) {
          throw new Error(
            res.data.message
          );
        }

        return (
          res.data.data || []
        );

      } catch (err) {

        return rejectWithValue(
          err.message
        );

      }
    }
  );

export const removeAggregation = createAsyncThunk(
  "ssccAggregation/removeAggregation",
  async (
    {
      parentId,
      childId
    },
    { rejectWithValue }
  ) => {
    try {

      const res = await postData(
        "/purchase/hierarchy-disconnect",
        {
          parent_sscc_id: parentId,
          child_sscc_id: childId
        }
      );

      if (res.data.error) {
        throw new Error(res.data.message);
      }

      return {
        parentId,
        childId
      };

    } catch (err) {

      return rejectWithValue(
        err.message ||
        "Failed to remove hierarchy"
      );

    }
  }
);

/**
 * Create aggregation (link parent-child)
 */
export const createAggregation = createAsyncThunk(
  'ssccAggregation/createAggregation',
  async ({ parentId, childId, purchaseId }, { rejectWithValue }) => {
    try {
      const payload = {
        parent_sscc_id: parentId,
        child_sscc_id: childId,
        purchase_id: purchaseId
      };
      const res = await postData('/purchase/hierarchy-connect', payload);

      if (res.data.error) {
        throw new Error(res.data.message);
      }

      return {
        success: true,
        data: res.data,
        parentId,
        childId
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create aggregation');
    }
  }
);

/**
 * Remove aggregation (unlink parent-child)
 */
// export const removeAggregation = createAsyncThunk(
//   'ssccAggregation/removeAggregation',
//   async ({ parentId, childId }, { rejectWithValue }) => {
//     try {
//       const res = await postData('/sscc/hierarchy/disconnect', {
//         parent_sscc_id: parentId,
//         child_sscc_id: childId
//       });

//       if (res.data.error) {
//         throw new Error(res.data.message);
//       }

//       return {
//         success: true,
//         data: res.data,
//         parentId,
//         childId
//       };
//     } catch (err) {
//       return rejectWithValue(err.message || 'Failed to remove aggregation');
//     }
//   }
// );

/**
 * Unpack SSCC (remove all children)
 */
export const unpackSSCC =
  createAsyncThunk(
    "ssccAggregation/unpackSSCC",

    async (
      {
        purchaseId,
        ssccId,
        ssccCode
      },
      { rejectWithValue }
    ) => {

      try {

        const res =
          await postData(
            "/purchase/unpack-initiate",
            {
              purchase_id:
                purchaseId,

              sscc_id:
                ssccId,

              sscc_code:
                ssccCode
            }
          );

        if (
          res.data.error
        ) {
          throw new Error(
            res.data.message
          );
        }

        return res.data;

      } catch (err) {

        return rejectWithValue(
          err.message
        );

      }
    }
  );

/**
 * Bulk create aggregations
 */
export const bulkCreateAggregations = createAsyncThunk(
  'ssccAggregation/bulkCreateAggregations',
  async ({ aggregations, purchaseId }, { rejectWithValue }) => {
    try {
      const res = await postData('/sscc/hierarchy/bulk-connect', {
        purchase_id: purchaseId,
        aggregations: aggregations
      });

      if (res.data.error) {
        throw new Error(res.data.message);
      }

      return {
        success: true,
        data: res.data,
        results: res.data.data || []
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create bulk aggregations');
    }
  }
);

/**
 * Validate hierarchy (check for cycles, duplicate parents)
 */
export const validateHierarchy = createAsyncThunk(
  'ssccAggregation/validateHierarchy',
  async ({ nodes, edges }, { rejectWithValue }) => {
    try {
      const res = await postData('/sscc/hierarchy/validate', { nodes, edges });

      if (res.data.error) {
        throw new Error(res.data.message);
      }

      return {
        valid: res.data.valid !== false,
        errors: res.data.errors || []
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to validate hierarchy');
    }
  }
);

/**
 * Save hierarchy
 */
export const saveHierarchy = createAsyncThunk(
  'ssccAggregation/saveHierarchy',
  async ({ purchaseId, nodes, edges }, { rejectWithValue }) => {
    try {
      const payload = {
        purchase_id: purchaseId,
        hierarchy: { nodes, edges }
      };
      const res = await postData('/sscc/hierarchy/define', payload);

      if (res.data.error) {
        throw new Error(res.data.message);
      }

      return {
        success: true,
        data: res.data
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to save hierarchy');
    }
  }
);

/**
 * Helper: Build relationship-based hierarchy
 */
function buildRelationshipHierarchy(data) {
  const nodes = [];
  const edges = [];

  // Process SSCCs - use positions from backend or auto-layout
  (data.ssccs || []).forEach((sscc, index) => {
    nodes.push({
      id: sscc.id,
      sscc_code: sscc.sscc_code,
      gs1_level: sscc.gs1_level || 'SSCC',
      status: sscc.status || 'pending',
      contents_description: sscc.contents_description || '',
      is_master: sscc.is_master || false,
      is_commissioned: sscc.is_commissioned || 'no',
      childCount: 0,
      // Use backend positions if available, otherwise auto-layout
      x: sscc.x || (index * 250),
      y: sscc.y || 0
    });
  });

  // Process aggregations
  (data.aggregations || []).forEach(agg => {
    edges.push({
      id: agg.id || `edge-${agg.parent_sscc_id}-${agg.child_sscc_id}`,
      source: agg.parent_sscc_id,
      target: agg.child_sscc_id
    });

    // Update child count
    const parent = nodes.find(n => n.id === agg.parent_sscc_id);
    if (parent) {
      parent.childCount = (parent.childCount || 0) + 1;
    }
  });

  // Find root nodes (no parent)
  const hasParent = new Set(edges.map(e => e.target));
  const rootNodes = nodes.filter(n => !hasParent.has(n.id));

  // Find leaf nodes (no children)
  const hasChildren = new Set(edges.map(e => e.source));
  const leafNodes = nodes.filter(n => !hasChildren.has(n.id));

  return {
    nodes,
    edges,
    rootNodes,
    leafNodes
  };
}